

import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, FileText, FileStack, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Header } from "../../components/Header";
import { Progress } from "../../components/ui/progress";
import {
  CustomizeFilenameDialog,
  type PdfDevelopmentsLimit,
} from "../../components/CustomizeFilenameDialog";
import * as meetingService from "../../services/meeting.service";
import * as projectService from "../../services/project.service";
import * as itemAtaService from "../../services/itemAta.service";
import * as desenvolvimentoService from "../../services/desenvolvimento.service";
import * as topicoService from "../../services/topico.service";
import * as participanteService from "../../services/participante.service";
import * as customizationService from "../../services/customization.service";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { Meeting } from "../../services/meeting.service";
import type { ItemAta } from "../../services/itemAta.service";
import type { Topico } from "../../services/topico.service";
import type { Desenvolvimento } from "../../services/desenvolvimento.service";
import type { Participante } from "../../services/participante.service";
import { translateMeetingData, type OrganizedDataForTranslation } from "../../services/translation.service";
import type { SupportedLanguage } from "../../i18n/config";
import { usePageTitle } from "../../hooks/usePageTitle";

interface MeetingWithDetails extends Meeting {
  topicos: Topico[];
  items: (ItemAta & {
    topico?: Topico;
    desenvolvimentos: Desenvolvimento[];
  })[];
  participantes: Participante[];
}

interface LoadingState {
  phase: 'project' | 'meetings' | 'processing' | 'finishing' | 'done';
  current: number;
  total: number;
  projectName?: string;
}

export default function AllMeetingsPreview() {
  const { t, i18n } = useTranslation('common');
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [projectName, setProjectName] = useState("");
  const [project, setProject] = useState<any>(null);
  usePageTitle(projectName ? `Todas as Atas — ${projectName}` : 'Todas as Atas');
  const [meetings, setMeetings] = useState<MeetingWithDetails[]>([]);
  const [showFilenameDialog, setShowFilenameDialog] = useState(false);
  const [loadingState, setLoadingState] = useState<LoadingState>({
    phase: 'project',
    current: 0,
    total: 0,
  });
  const [headerImageUrl, setHeaderImageUrl] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState<{ current: number; total: number } | null>(null);
  const [translatedMeetings, setTranslatedMeetings] = useState<MeetingWithDetails[] | null>(null);
  const [pdfDevelopmentsLimit, setPdfDevelopmentsLimit] =
    useState<PdfDevelopmentsLimit>(null);

  const isLoading = loadingState.phase !== 'done';

  // Calculate progress percentage based on phase
  const progressPercentage = useMemo(() => {
    const { phase, current, total } = loadingState;
    switch (phase) {
      case 'project':
        return 5;
      case 'meetings':
        return 15;
      case 'processing':
        // Processing goes from 15% to 85%
        if (total === 0) return 15;
        return 15 + Math.round((current / total) * 70);
      case 'finishing':
        return 95;
      case 'done':
        return 100;
      default:
        return 0;
    }
  }, [loadingState]);

  // Get loading message based on phase
  const loadingMessage = useMemo(() => {
    const { phase, current, total, projectName } = loadingState;
    switch (phase) {
      case 'project':
        return t('allMeetingsPreview.loading_project');
      case 'meetings':
        return projectName ? `${t('search')} ${projectName}...` : `${t('search')}...`;
      case 'processing':
        if (total === 0) return t('allMeetingsPreview.processing');
        if (current === total) return t('allMeetingsPreview.processing');
        return t('allMeetingsPreview.processing_meeting', { current, total });
      case 'finishing':
        return t('allMeetingsPreview.processing');
      case 'done':
        return t('success');
      default:
        return t('allMeetingsPreview.loading');
    }
  }, [loadingState, t]);

  // Get subtitle message
  const loadingSubtitle = useMemo(() => {
    const { phase, current, total } = loadingState;
    switch (phase) {
      case 'project':
        return 'Obtendo informações do projeto';
      case 'meetings':
        return 'Listando todas as atas disponíveis';
      case 'processing':
        if (total === 0) return '';
        const remaining = total - current;
        if (remaining <= 0) return 'Organizando os dados...';
        if (remaining === 1) return 'Falta apenas 1 ata';
        return `Faltam ${remaining} atas`;
      case 'finishing':
        return 'Preparando a visualização';
      default:
        return '';
    }
  }, [loadingState]);

  useEffect(() => {
    loadAllMeetings();
    loadCustomization();
  }, [projectId]);

  const loadCustomization = async () => {
    try {
      const customization = await customizationService.getUserCustomization();
      setHeaderImageUrl(customization.header_image_url);
    } catch (error) {
      console.error("Erro ao carregar personalização:", error);
    }
  };

  const loadAllMeetings = async () => {
    if (!projectId) return;

    try {
      // Phase 1: Loading project
      setLoadingState({ phase: 'project', current: 0, total: 0 });

      const project = await projectService.getProjectById(projectId);
      setProjectName(project.name);

      // Phase 2: Loading meetings list
      setLoadingState({ phase: 'meetings', current: 0, total: 0, projectName: project.name });

      const meetingsResponse = await meetingService.getProjectMeetings(projectId);
      const allMeetings = meetingsResponse.meetings;
      const sortedMeetings = allMeetings.sort((a, b) => a.number - b.number);

      // Phase 3: Processing each meeting
      const total = sortedMeetings.length;
      setLoadingState({ phase: 'processing', current: 0, total, projectName: project.name });

      const meetingsWithDetails: MeetingWithDetails[] = [];

      for (let i = 0; i < sortedMeetings.length; i++) {
        const meeting = sortedMeetings[i];

        const topicos = await topicoService.getTopicosByAtaId(meeting.id);
        const items = await itemAtaService.getItemsByAtaId(meeting.id);

        const itemsWithDetails = await Promise.all(
          items.map(async (item) => {
            const desenvolvimentos = await desenvolvimentoService.getDesenvolvimentosByItemId(item.id);
            const topico = topicos.find(t => t.id === item.topico_id);

            return {
              ...item,
              topico,
              desenvolvimentos,
            };
          })
        );

        const participantes = await participanteService.getParticipantesByAtaId(meeting.id);

        meetingsWithDetails.push({
          ...meeting,
          topicos,
          items: itemsWithDetails,
          participantes,
        });

        // Update progress
        setLoadingState({ phase: 'processing', current: i + 1, total, projectName: project.name });
      }

      // Phase 4: Finishing
      setLoadingState({ phase: 'finishing', current: total, total, projectName: project.name });

      // Small delay to show the finishing message
      await new Promise(resolve => setTimeout(resolve, 300));

      setMeetings(meetingsWithDetails);

      // Phase 5: Done
      setLoadingState({ phase: 'done', current: total, total, projectName: project.name });
    } catch (error) {
      console.error("Erro ao carregar atas:", error);
      setLoadingState({ phase: 'done', current: 0, total: 0 });
    }
  };

  const buildOrganizedData = (meeting: MeetingWithDetails): OrganizedDataForTranslation => ({
    topicos: meeting.topicos.map((topico) => ({
      ...topico,
      items: meeting.items
        .filter((item) => item.topico_id === topico.id)
        .map((item) => ({ ...item })),
    })),
    itemsSemTopico: meeting.items
      .filter((item) => !item.topico_id)
      .map((item) => ({ ...item })),
  });

  const mergeTranslatedMeeting = (
    original: MeetingWithDetails,
    result: Awaited<ReturnType<typeof translateMeetingData>>
  ): MeetingWithDetails => {
    const allItems = [
      ...result.organizedData.topicos.flatMap((t) => t.items),
      ...result.organizedData.itemsSemTopico,
    ];
    const itemMap = new Map(allItems.map((item) => [item.id, item]));
    const topicoMap = new Map(result.organizedData.topicos.map((t) => [t.id, t]));

    return {
      ...original,
      name: result.meeting.name,
      location: result.meeting.location,
      topicos: original.topicos.map((t) => ({
        ...t,
        titulo: topicoMap.get(t.id)?.titulo ?? t.titulo,
      })),
      items: original.items.map((item) => {
        const tr = itemMap.get(item.id);
        return {
          ...item,
          descricao: tr?.descricao ?? item.descricao,
          desenvolvimentos: item.desenvolvimentos.map((dev) => {
            const trDev = tr?.desenvolvimentos.find((d) => d.id === dev.id);
            return trDev ? { ...dev, texto: trDev.texto ?? dev.texto } : dev;
          }),
        };
      }),
    };
  };

  const handleExportPDF = async (
    customFilename: string,
    exportLanguage: SupportedLanguage,
    developmentsLimit: PdfDevelopmentsLimit = null
  ) => {
    setPdfDevelopmentsLimit(developmentsLimit);

    const originalTitle = document.title;
    const currentLanguage = (i18n.language as SupportedLanguage) || 'pt-BR';
    const needsTranslation = exportLanguage !== currentLanguage;

    if (needsTranslation) {
      setIsTranslating(true);
      try {
        const translated: MeetingWithDetails[] = [];
        for (let idx = 0; idx < meetings.length; idx++) {
          setTranslationProgress({ current: idx + 1, total: meetings.length });
          const m = meetings[idx];
          const result = await translateMeetingData(
            m,
            buildOrganizedData(m),
            currentLanguage,
            exportLanguage
          );
          translated.push(mergeTranslatedMeeting(m, result));
        }
        setTranslatedMeetings(translated);
        await i18n.changeLanguage(exportLanguage);
      } catch (err) {
        console.error('Falha na tradução, prosseguindo sem traduzir:', err);
        await i18n.changeLanguage(exportLanguage);
      }
      setIsTranslating(false);
      setTranslationProgress(null);
    }

    document.title = customFilename;

    setTimeout(() => {
      const imagesToLoad = document.querySelectorAll('img[src]');
      const imagePromises = Array.from(imagesToLoad).map((img) => {
        return new Promise((resolve) => {
          if ((img as HTMLImageElement).complete) {
            resolve(true);
          } else {
            img.addEventListener('load', () => resolve(true));
            img.addEventListener('error', () => resolve(false));
            setTimeout(() => resolve(false), 3000);
          }
        });
      });

      Promise.all(imagePromises).then(() => {
        const html = document.documentElement;
        const wasDark = html.classList.contains('dark');
        if (wasDark) html.classList.remove('dark');

        window.print();

        setTimeout(() => {
          if (wasDark) html.classList.add('dark');
          document.title = originalTitle;
          if (needsTranslation) {
            i18n.changeLanguage(currentLanguage);
            setTranslatedMeetings(null);
          }
        }, 1000);
      });
    }, 300);
  };

  const getVisibleDevelopments = (developments: Desenvolvimento[]) => {
    if (pdfDevelopmentsLimit === null) return developments;
    return developments.slice(-pdfDevelopmentsLimit);
  };

  const getHiddenDevelopmentsCount = (developments: Desenvolvimento[]) => {
    if (pdfDevelopmentsLimit === null) return 0;
    return Math.max(developments.length - pdfDevelopmentsLimit, 0);
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-muted/30 to-muted/50 flex items-center justify-center">
          <div className="w-full max-w-md mx-auto px-6">
            {/* Card container */}
            <div className="bg-card rounded-2xl shadow-lg border border-border p-8 space-y-6">

              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" />
                  <div className="relative bg-primary/10 p-4 rounded-full">
                    <FileStack className="w-8 h-8 text-primary animate-pulse" />
                  </div>
                </div>
              </div>


              <div className="text-center space-y-2">
                <h2 className="text-lg font-semibold text-foreground transition-all duration-300">
                  {loadingMessage}
                </h2>
                <p className="text-sm text-muted-foreground transition-all duration-300 min-h-[20px]">
                  {loadingSubtitle}
                </p>
              </div>

              {/* Progress bar */}
              <div className="space-y-3">
                <Progress
                  value={progressPercentage}
                  className="h-2 bg-muted"
                />
                <div className="flex justify-between items-center text-xs text-muted-foreground">
                  <span>{progressPercentage}%</span>
                  {loadingState.total > 0 && (
                    <span>{loadingState.current}/{loadingState.total} atas</span>
                  )}
                </div>
              </div>

              {/* Animated dots */}
              <div className="flex justify-center gap-1.5">
                <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>

            {/* Helper text below card */}
            <p className="text-center text-xs text-muted-foreground mt-4">
              Isso pode levar alguns segundos dependendo da quantidade de atas
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />

      <div className="no-print bg-background border-b border-border sticky top-16 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            {/* Botão voltar */}
            <button
              onClick={() => navigate(`/projects/${projectId}/meetings`)}
              className="p-2 hover:bg-muted rounded-md transition-colors flex-shrink-0"
              aria-label={t('allMeetingsPreview.back_to_meetings')}
            >
              <ArrowLeft size={18} className="text-muted-foreground" />
            </button>

            {/* Info principal */}
            <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="text-lg font-semibold text-foreground truncate">
                  {t('meetings.export_all')}
                </h1>
                <span className="text-muted-foreground/40 hidden sm:inline">•</span>
                <span className="text-sm text-muted-foreground truncate hidden sm:inline">{projectName}</span>
              </div>

              {/* Metadata inline */}
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>{meetings.length} {meetings.length === 1 ? t('meetings.meetings_count_singular', { count: 1 }) : t('meetings.meetings_count_plural', { count: meetings.length })}</span>
              </div>
            </div>

            {/* Ações */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                onClick={() => setShowFilenameDialog(true)}
                variant="default"
                size="sm"
                className="gap-2"
                disabled={isTranslating}
              >
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">{isTranslating ? 'Traduzindo...' : t('editor.export_pdf')}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <CustomizeFilenameDialog
        open={showFilenameDialog}
        onClose={() => setShowFilenameDialog(false)}
        onExport={handleExportPDF}
        projectCode={project?.project_code}
        isMultipleMeetings={true}
        currentLanguage={(i18n.language as SupportedLanguage) || 'pt-BR'}
        showDevelopmentsLimitOption
        defaultDevelopmentsLimit={pdfDevelopmentsLimit}
      />

      <div className="bg-muted/30 min-h-screen print:bg-white print:min-h-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 print:p-0">
          {/* Capa */}
          <div className="mb-8 page-break print:mb-0 print:h-screen print:flex print:flex-col print:justify-center print:items-center">
            <div className="bg-white rounded-lg border border-border shadow-sm p-12 text-center print:border-0 print:shadow-none">
              {headerImageUrl && (
                <div className="mb-8 flex justify-center">
                  <img
                    src={headerImageUrl}
                    alt="Logo"
                    className="h-24 w-auto max-w-[400px] object-contain"
                  />
                </div>
              )}

              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 print:text-5xl">
                {projectName}
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-2">
                Compilação de Atas
              </p>
              <p className="text-base sm:text-lg text-gray-500">
                {meetings.length} {meetings.length === 1 ? "ata" : "atas"}
              </p>
              <p className="text-xs sm:text-sm text-gray-400 mt-8">
                Gerado em {format(new Date(), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}
              </p>
            </div>
          </div>

          {/* Atas */}
          {(translatedMeetings ?? meetings).map((meeting, index) => (
            <div key={meeting.id} className={`mb-8 ${index > 0 ? "page-break" : ""}`}>
              <div className="bg-white rounded-lg border border-border shadow-sm overflow-hidden print:border print:rounded-none">
                {/* Header da ata */}
                <div className="border-b border-border p-6 print:p-4">
                  <div className="flex justify-between items-start gap-4 mb-4">
                    <div className="flex-1">
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 print:text-xl">
                        ATA DE REUNIÃO N.º {meeting.number}
                      </h2>
                      {meeting.name && (
                        <p className="text-base sm:text-lg text-gray-700">{meeting.name}</p>
                      )}
                      <div className="text-xs sm:text-sm text-gray-600 mt-2">
                        {format(parseISO(meeting.date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                      </div>
                    </div>

                    {headerImageUrl && (
                      <div className="hidden print:block ml-4 flex-shrink-0">
                        <img
                          src={headerImageUrl}
                          alt="Logo"
                          className="h-16 w-auto max-w-[200px] object-contain"
                        />
                      </div>
                    )}
                  </div>

                  {/* Badge de status */}
                  <div className="flex items-center gap-2">
                    {meeting.status === "final" ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-md">
                        Finalizada
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-md">
                        Em Andamento
                      </span>
                    )}
                  </div>
                </div>

                {/* Corpo da ata */}
                <div className="p-6 print:p-4">
                  {/* Local */}
                  {meeting.location && (
                    <div className="mb-6 p-3 bg-muted/50 rounded-md text-sm print:text-xs">
                      <span className="font-semibold text-gray-900">Local:</span>{" "}
                      <span className="text-gray-700">{meeting.location}</span>
                    </div>
                  )}

                  {/* Participantes */}
                  {meeting.participantes.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-semibold text-sm mb-3 text-gray-900">Participantes</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm print:text-xs">
                        {meeting.participantes.map((p) => (
                          <div key={p.id} className="flex items-start gap-2 text-gray-700">
                            <span className="text-gray-400 mt-0.5">•</span>
                            <span>
                              {p.nome} {p.email && <span className="text-gray-500">({p.email})</span>}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tópicos */}
                  {meeting.topicos.length > 0 ? (
                    meeting.topicos.map((topico) => {
                      const topicoItems = meeting.items.filter(
                        (item) => item.topico_id === topico.id
                      );

                      return (
                        <div key={topico.id} className="mb-6 print:mb-4">
                          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-border print:text-lg">
                            {topico.titulo}
                          </h3>

                          {topicoItems.length === 0 ? (
                            <p className="text-gray-500 italic text-sm py-4 px-3 bg-muted/30 rounded-md">
                              Nenhum item registrado neste tópico.
                            </p>
                          ) : (
                            <div className="space-y-4">
                              {topicoItems.map((item, itemIndex) => (
                                <div key={item.id} className="flex items-start gap-3 p-4 rounded-md hover:bg-muted/30 transition-colors print:p-2 print:hover:bg-transparent">
                                  <span className="font-semibold text-gray-700 text-sm min-w-[2rem] mt-0.5">
                                    {itemIndex + 1}.
                                  </span>
                                  <div className="flex-1 space-y-3">
                                    <p className="text-gray-900 text-sm sm:text-base leading-relaxed">{item.descricao}</p>

                                    {(item.entidade || item.prazo) && (
                                      <div className="flex flex-wrap gap-3 text-xs sm:text-sm print:text-xs">
                                        {item.entidade && (
                                          <div className="flex items-center gap-1 text-gray-600">
                                            <span className="font-semibold">Responsável:</span>
                                            <span>{item.entidade}</span>
                                          </div>
                                        )}
                                        {item.prazo && (
                                          <div className="flex items-center gap-1 text-gray-600">
                                            <span className="font-semibold">Prazo:</span>
                                            <span>
                                              {(() => {
                                                try {
                                                  const dateStr = item.prazo.includes('T')
                                                    ? item.prazo
                                                    : item.prazo + 'T00:00:00';
                                                  return format(parseISO(dateStr), "dd/MM/yyyy", { locale: ptBR });
                                                } catch {
                                                  return item.prazo;
                                                }
                                              })()}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Status badge */}
                                    <div>
                                      {item.concluido ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-md">
                                          Concluído
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-md">
                                          Em andamento
                                        </span>
                                      )}
                                    </div>

                                    {/* Desenvolvimentos */}
                                    {item.desenvolvimentos.length > 0 && (
                                      <div className="pl-4 border-l-2 border-blue-200 space-y-2">
                                        {getHiddenDevelopmentsCount(item.desenvolvimentos) > 0 && (
                                          <div className="text-xs text-gray-500 italic">
                                            ... ({getHiddenDevelopmentsCount(item.desenvolvimentos)} atualizações anteriores)
                                          </div>
                                        )}
                                        {getVisibleDevelopments(item.desenvolvimentos).map((dev) => (
                                          <div
                                            key={dev.id}
                                            className="text-xs sm:text-sm text-gray-700 print:text-xs"
                                          >
                                            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-md mr-2 font-medium">
                                              A{dev.ata_numero}
                                            </span>
                                            <span className="whitespace-pre-line">{dev.texto}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="mb-6">
                      <div className="space-y-4">
                        {meeting.items.map((item, itemIndex) => (
                          <div key={item.id} className="flex items-start gap-3 p-4 rounded-md hover:bg-muted/30 transition-colors print:p-2 print:hover:bg-transparent">
                            <span className="font-semibold text-gray-700 text-sm min-w-[2rem] mt-0.5">
                              {itemIndex + 1}.
                            </span>
                            <div className="flex-1 space-y-3">
                              <p className="text-gray-900 text-sm sm:text-base leading-relaxed">{item.descricao}</p>

                              {(item.entidade || item.prazo) && (
                                <div className="flex flex-wrap gap-3 text-xs sm:text-sm print:text-xs">
                                  {item.entidade && (
                                    <div className="flex items-center gap-1 text-gray-600">
                                      <span className="font-semibold">Responsável:</span>
                                      <span>{item.entidade}</span>
                                    </div>
                                  )}
                                  {item.prazo && (
                                    <div className="flex items-center gap-1 text-gray-600">
                                      <span className="font-semibold">Prazo:</span>
                                      <span>
                                        {(() => {
                                          try {
                                            const dateStr = item.prazo.includes('T')
                                              ? item.prazo
                                              : item.prazo + 'T00:00:00';
                                            return format(parseISO(dateStr), "dd/MM/yyyy", { locale: ptBR });
                                          } catch {
                                            return item.prazo;
                                          }
                                        })()}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}

                              <div>
                                {item.concluido ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-md">
                                    Concluído
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-md">
                                    Em andamento
                                  </span>
                                )}
                              </div>

                              {item.desenvolvimentos.length > 0 && (
                                <div className="pl-4 border-l-2 border-blue-200 space-y-2">
                                  {getHiddenDevelopmentsCount(item.desenvolvimentos) > 0 && (
                                    <div className="text-xs text-gray-500 italic">
                                      ... ({getHiddenDevelopmentsCount(item.desenvolvimentos)} atualizações anteriores)
                                    </div>
                                  )}
                                  {getVisibleDevelopments(item.desenvolvimentos).map((dev) => (
                                    <div
                                      key={dev.id}
                                      className="text-xs sm:text-sm text-gray-700 print:text-xs"
                                    >
                                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-md mr-2 font-medium">
                                        A{dev.ata_numero}
                                      </span>
                                      <span className="whitespace-pre-line">{dev.texto}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {isTranslating && (
        <div className="fixed inset-0 bg-white/75 z-50 flex items-center justify-center no-print">
          <div className="flex flex-col items-center gap-3 p-6 bg-white rounded-xl shadow-lg border border-border" style={{ minWidth: 260 }}>
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
            <p className="text-sm font-medium text-gray-700">Traduzindo conteúdo...</p>
            {translationProgress && (
              <>
                <p className="text-xs text-gray-500">
                  Ata {translationProgress.current} de {translationProgress.total}
                </p>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className="bg-primary h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${Math.round((translationProgress.current / translationProgress.total) * 100)}%` }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 15mm 10mm;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            box-sizing: border-box;
          }

          html, body {
            width: 210mm;
            height: 297mm;
            margin: 0;
            padding: 0;
            overflow: visible;
          }

          body {
            font-size: 9pt;
            line-height: 1.3;
            zoom: 0.68;
          }

          .no-print {
            display: none !important;
          }
          
          .page-break {
            page-break-before: always;
            break-before: page;
          }

          
          h1 {
            font-size: 14pt;
            margin: 0 0 8pt 0;
          }

          h2 {
            font-size: 12pt;
            margin: 8pt 0 6pt 0;
          }

          h3 {
            font-size: 10pt;
            margin: 6pt 0 4pt 0;
          }

          p, td, th, span, div, li {
            font-size: 8pt;
            line-height: 1.3;
          }

          
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 8pt;
            page-break-inside: auto;
            table-layout: fixed;
          }

          thead {
            display: table-header-group;
          }

          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }

          th {
            padding: 4pt 3pt;
            font-size: 7pt;
            background-color: #f3f4f6 !important;
            border: 0.5pt solid #d1d5db;
          }

          td {
            padding: 4pt 3pt;
            font-size: 8pt;
            border: 0.5pt solid #d1d5db;
            word-wrap: break-word;
          }

          
          .mb-6, .mb-8 {
            margin-bottom: 8pt !important;
          }

          .mb-4 {
            margin-bottom: 6pt !important;
          }

          .p-4, .p-6, .p-8 {
            padding: 6pt !important;
          }

          
          .bg-green-100 {
            background-color: #dcfce7 !important;
          }
          
          .text-green-800 {
            color: #166534 !important;
          }
          
          .bg-yellow-100 {
            background-color: #fef3c7 !important;
          }
          
          .text-yellow-800 {
            color: #854d0e !important;
          }
          
          .bg-blue-100 {
            background-color: #dbeafe !important;
          }
          
          .text-blue-800 {
            color: #1e40af !important;
          }
          
          .bg-gray-50 {
            background-color: #f9fafb !important;
          }

          .bg-gray-100 {
            background-color: white !important;
          }

          
          img {
            max-height: 40pt;
            max-width: 150pt;
          }

          
          .shadow-sm {
            box-shadow: none !important;
          }
        }
      `}</style>
    </>
  );
}
